import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  type FeedCodeView,
  type FeedScopeEntry,
  type FeedProposalView,
  type FeedRevokeImpact,
  type FeedView,
  type InstanceDetail,
  type MintedFeedCode,
} from "@keydials/api-client";
import { DEFAULT_SEGMENT_NOUN, segmentNoun, type SegmentNoun } from "@keydials/dial-workspace";
import { Icon } from "../Icon";
import { Modal } from "./Modal";
import { SettingsCard } from "../../settings/SettingsCard";
import { FeedScopeCanvas } from "./FeedScopeCanvas";
import { FeedWiringModal } from "./FeedWiringBoard";
import { FeedProposalsPanel } from "./FeedProposalsPanel";
import { ShareRefusalNote } from "../../settings/ShareRefusalNote";
import { AddToGroupModal } from "../../groups/AddToGroupModal";
import { ownSections } from "../../feedWiring";
import { api, getAccessToken } from "../../auth/AuthContext";

/**
 * Feeds (KEY-308): what this dial sends to another organisation, and what it receives from one.
 *
 * The panel is split by direction because a feed has one, and a single undifferentiated list would be the
 * first thing to confuse someone. "Fed in" is where you mint a code and answer offers; "Feeding out" is
 * where you redeem someone else's code and choose what to disclose.
 *
 * Deliberately distinct from membership: People and Guests (the sections above this one in Dial Settings)
 * say who may SEE this dial inside your own organisation, a feed is what one dial sends another across
 * organisations. Neither word is borrowed for the other. (The old Sharing button this used to sit beside
 * went in KEY-456.)
 *
 * What to feed is painted onto the dial itself (KEY-257 phase 2) rather than ticked off a list, because
 * the thing being described is spatial and people already picture it that way.
 */

const fieldStyle = {
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  padding: "10px 12px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface-2)",
  color: "var(--text)",
  font: "inherit",
  fontSize: 14,
  outline: "none",
} as const;

const headingStyle = {
  fontSize: 12.5,
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: "uppercase",
  color: "var(--text-muted)",
  margin: "0 0 8px",
} as const;

const cardStyle = {
  border: "var(--border-w) solid var(--border)",
  borderRadius: "var(--radius-sm)",
  padding: "12px 14px",
  display: "flex",
  flexDirection: "column",
  gap: 10,
} as const;

const hintStyle = { fontSize: 12, color: "var(--text-faint)", margin: 0 } as const;

/** "2 sections · 3 action points", or an honest blank when nothing has been chosen. `noun` is the word of the dial the
 *  scopes sit on (KEY-721) — known for what THIS dial sends, not for what another dial feeds in. */
function scopeSummary(scopes: FeedScopeEntry[], noun: SegmentNoun = DEFAULT_SEGMENT_NOUN): string {
  if (scopes.length === 0) return "nothing yet";
  const sections = scopes.filter((s) => !s.segmentKey).length;
  const segments = scopes.filter((s) => s.segmentKey).length;
  const parts: string[] = [];
  if (sections > 0) parts.push(`${sections} ${sections === 1 ? "section" : "sections"}`);
  if (segments > 0) parts.push(`${segments} ${segments === 1 ? noun.one : noun.many}`);
  const open = scopes.some((s) => s.includeFutureChildren) ? " (plus anything added later)" : "";
  return parts.join(" · ") + open;
}

function minutesLeft(iso: string, now: Date): number {
  return Math.ceil((new Date(iso).getTime() - now.getTime()) / 60_000);
}

/** A live feed's one-line state, in the vocabulary of the feature rather than the database. */
function stateText(feed: FeedView): string {
  switch (feed.state) {
    case "Draft":
      return feed.direction === "out" ? "Not offered yet" : "Being prepared";
    case "Offered":
      return feed.direction === "in" ? "Waiting for you" : "Waiting for them";
    case "Accepted":
      return feed.expiresAtUtc ? `Live until ${new Date(feed.expiresAtUtc).toLocaleDateString()}` : "Live";
    case "Declined":
      return "Declined";
    default:
      return "Stopped";
  }
}

/**
 * What this dial sends to, and receives from, other organisations (KEY-257).
 *
 * Was a modal behind a top-bar button; now a section of the Settings tab. The modals *inside* it stay
 * modals: minting a code, painting a scope and confirming a stop are all transient decisions taken
 * against the list, and inlining them would push the list they refer to off the screen.
 */
export function FeedsSettingsPanel({
  instanceId,
  dialName,
  detail,
  refusal = null,
}: {
  instanceId: string;
  dialName: string;
  /** The whole dial, because both halves of this panel need different parts of it: the paint canvas
   * needs the structure and the declared fields, the wiring board needs the palettes and axes too. */
  detail: InstanceDetail;
  /**
   * Why this dial's organisation won't let it be fed out (KEY-556), or null. Shuts only the sharer's acts —
   * redeeming a code, choosing what to send, offering it — which are the three the server gates. "Fed in",
   * wiring and Stop stay: none of them hands this dial to anybody.
   */
  refusal?: string | null;
}) {
  const [feeds, setFeeds] = useState<FeedView[] | null>(null);
  const [codes, setCodes] = useState<FeedCodeView[] | null>(null);
  const [minted, setMinted] = useState<MintedFeedCode | null>(null);
  const [copied, setCopied] = useState(false);
  const [expect, setExpect] = useState("");
  const [redeem, setRedeem] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which outgoing feed is having its scope edited, and the scope being painted for it.
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<FeedScopeEntry[]>([]);
  const [openEnded, setOpenEnded] = useState(false);
  // Which incoming feed is being wired into this dial.
  const [wiring, setWiring] = useState<string | null>(null);
  // Changes a feed is waiting on this dial's owner for, and the feed being stopped (with what that costs).
  const [proposals, setProposals] = useState<FeedProposalView[]>([]);
  const [stopping, setStopping] = useState<{ feedId: string; direction: "in" | "out"; impact: FeedRevokeImpact } | null>(null);
  // The incoming feed whose dial is being put into one of your groups (KEY-564), and the group each one joined.
  const [grouping, setGrouping] = useState<FeedView | null>(null);
  const [groupedInto, setGroupedInto] = useState<Record<string, string>>({});
  // Your own sections only. A section another organisation feeds you is a read-through of their work:
  // you cannot pass it on, and offering it here would let someone paint a scope the server then refuses.
  const attributes = detail.nodeAttributes ?? [];
  // KEY-721: this dial's word for an action point (KEY-539), for the copy about what it sends.
  const noun = segmentNoun(detail);
  const paintSections = ownSections(detail.sections).map((s) => ({
    key: s.key,
    label: s.label,
    segments: s.segments.map((g) => ({
      key: g.key,
      label: g.name,
      items: g.items.map((i) => ({ key: i.key, name: i.name })),
    })),
  }));
  const now = new Date();

  const reload = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      const [f, c, p] = await Promise.all([
        api.listFeeds(token, instanceId),
        api.listFeedCodes(token, instanceId),
        api.listFeedProposals(token, instanceId).catch(() => []),
      ]);
      setFeeds(f);
      setCodes(c);
      setProposals(p);
    } catch {
      setFeeds([]);
      setCodes([]);
      setProposals([]);
    }
  }, [instanceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function run(action: () => Promise<unknown>) {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await action();
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  async function mint() {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const code = await api.mintFeedCode(token, instanceId, {
        expectedCounterparty: expect.trim() || null,
        lifetimeMinutes: null,
      });
      setMinted(code);
      setCopied(false);
      setExpect("");
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't create a code.");
    } finally {
      setBusy(false);
    }
  }

  async function redeemCode() {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const feed = await api.redeemFeedCode(token, redeem, instanceId);
      setRedeem("");
      await reload();
      // Straight into choosing what to send — a draft that is never composed helps nobody.
      setEditing(feed.feedId);
      setDraft([]);
      setOpenEnded(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't use that code.");
    } finally {
      setBusy(false);
    }
  }

  async function decide(proposal: FeedProposalView, accept: boolean) {
    await run(() => api.decideFeedProposal(getAccessToken()!, proposal.id, accept));
  }

  /**
   * Stopping is confirmed rather than immediate, because the two modes end differently and the difference
   * is invisible: watching a feed vanishes cleanly, while a wired one has already copied values into rows
   * that stay. Someone who stops expecting "their data goes away" would be right about half of it.
   */
  async function askToStop(feed: FeedView) {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const impact = await api.feedRevokeImpact(token, feed.id);
      setStopping({ feedId: feed.id, direction: feed.direction, impact });
    } catch {
      // No answer on what it costs is not a reason to block the action — just do it without the number.
      setStopping({ feedId: feed.id, direction: feed.direction, impact: { valuesRemaining: 0, liveWires: 0 } });
    } finally {
      setBusy(false);
    }
  }

  function startEditing(feed: FeedView) {
    setEditing(feed.id);
    setDraft(feed.scopes);
    setOpenEnded(feed.scopes.some((s) => s.includeFutureChildren));
    setError(null);
  }

  /** The open-ended promise applies to whole-section rows only — a single action point has no children
   * of its own to acquire. Re-stamping the live draft keeps the switch and the painted scope agreeing. */
  function setOpenEndedAcross(next: boolean) {
    setOpenEnded(next);
    setDraft((prev) => prev.map((s) => ({ ...s, includeFutureChildren: next && !s.segmentKey })));
  }

  const incoming = (feeds ?? []).filter((f) => f.direction === "in");
  const outgoing = (feeds ?? []).filter((f) => f.direction === "out");
  const liveCodes = (codes ?? []).filter((c) => !c.consumedAtUtc && minutesLeft(c.expiresAtUtc, now) > 0);

  return (
    <SettingsCard title="Feeds" subtitle={`What “${dialName}” sends and receives`}>
      {error && <p style={{ margin: "0 0 10px", fontSize: 12.5, color: "var(--st-overdue)" }}>{error}</p>}

      <FeedProposalsPanel proposals={proposals} detail={detail} busy={busy} onDecide={(p, accept) => void decide(p, accept)} />

      {/* ---- Fed in ---------------------------------------------------------------------------- */}
      <h3 style={headingStyle}>Fed in · what other dials send you</h3>

      <div style={{ ...cardStyle, marginBottom: 14 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>Invite someone to feed this dial</div>
        <p style={hintStyle}>
          Create a code and read it out to them. They redeem it on their own dial, choose what to send, and
          it only reaches you once you accept.
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <label style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-muted)" }}>Who you expect (optional)</span>
            <input
              value={expect}
              onChange={(e) => setExpect(e.target.value)}
              placeholder="Bob&rsquo;s Builders"
              style={fieldStyle}
              aria-label="Who you expect to redeem this code"
            />
          </label>
          <button className="kd-btn-primary" onClick={() => void mint()} disabled={busy}>
            <Icon name="plus" size={16} />
            Create a feed code
          </button>
        </div>

        {minted && (
          <div
            style={{
              border: "var(--border-w) solid var(--accent-border)",
              background: "var(--accent-soft)",
              borderRadius: "var(--radius-sm)",
              padding: "12px 14px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <code style={{ fontSize: 22, fontWeight: 700, letterSpacing: 2, color: "var(--text)" }}>{minted.code}</code>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <button
                className="kd-btn"
                onClick={() => {
                  void navigator.clipboard?.writeText(minted.code);
                  setCopied(true);
                }}
              >
                {copied ? "Copied" : "Copy code"}
              </button>
              <span style={hintStyle}>
                Read it out or show it. It expires in {Math.max(0, minutesLeft(minted.expiresAtUtc, now))} minutes,
                works once, and you won&rsquo;t see it again.
              </span>
            </div>
          </div>
        )}

        {liveCodes.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {liveCodes.map((c) => (
              <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, padding: "4px 0" }}>
                <Icon name="lock" size={14} />
                <span style={{ flex: 1, color: "var(--text-muted)" }}>
                  {c.expectedCounterparty ? `For ${c.expectedCounterparty}` : "Unlabelled code"} · expires in{" "}
                  {Math.max(0, minutesLeft(c.expiresAtUtc, now))} min
                </span>
                <button
                  onClick={() => void run(() => api.revokeFeedCode(getAccessToken()!, instanceId, c.id))}
                  disabled={busy}
                  aria-label="Cancel this code"
                  style={{ border: "none", background: "none", cursor: "pointer", color: "var(--text-faint)", display: "flex", padding: 4 }}
                >
                  <Icon name="trash" size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {incoming.length === 0 && (
        <p style={{ ...hintStyle, marginBottom: 18 }}>Nobody is feeding this dial yet.</p>
      )}
      {incoming.map((f) => (
        <div key={f.id} style={{ ...cardStyle, marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>{f.sourceOrganisationName}</span>
            <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}>
              is feeding you {scopeSummary(f.scopes)} from “{f.sourceDialName}”
            </span>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-faint)" }}>{stateText(f)}</div>
          {f.state === "Offered" && (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="kd-btn-primary" disabled={busy} onClick={() => void run(() => api.acceptFeed(getAccessToken()!, f.id))}>
                Accept
              </button>
              <button className="kd-btn" disabled={busy} onClick={() => void run(() => api.declineFeed(getAccessToken()!, f.id))}>
                Decline
              </button>
            </div>
          )}
          {f.state === "Accepted" && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <button className="kd-btn" disabled={busy} onClick={() => setWiring(f.id)}>
                Wire it into your dial
              </button>
              {/* A fed dial already sits in My Dials, but nothing there marks it as fed — this is where
                  someone learns they have it, so this is where to offer grouping it with their own. */}
              <button className="kd-btn" disabled={busy} onClick={() => setGrouping(f)}>
                Add to a group
              </button>
              <button className="kd-btn" disabled={busy} onClick={() => void askToStop(f)}>
                Stop feeding
              </button>
              <span style={hintStyle}>
                {groupedInto[f.id]
                  ? `“${f.sourceDialName}” is in your group “${groupedInto[f.id]}” in My Dials.`
                  : `“${f.sourceDialName}” now appears in My Dials.`}
              </span>
            </div>
          )}
        </div>
      ))}
      {grouping && (
        <AddToGroupModal
          instanceId={grouping.sourceInstanceId}
          dialName={grouping.sourceDialName}
          onClose={() => setGrouping(null)}
          onAdded={(groupName) => {
            setGroupedInto((prev) => ({ ...prev, [grouping.id]: groupName }));
            setGrouping(null);
          }}
        />
      )}

      {/* ---- Feeding out ----------------------------------------------------------------------- */}
      <h3 style={{ ...headingStyle, marginTop: 22 }}>Feeding out · what this dial sends</h3>

      {refusal ? (
        <div style={{ marginBottom: 14 }}>
          <ShareRefusalNote text={refusal} />
        </div>
      ) : (
        <div style={{ ...cardStyle, marginBottom: 14 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>Feed this dial to someone</div>
          <p style={hintStyle}>Enter the code they read out to you. Nothing is sent until you choose what to send and they accept.</p>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={redeem}
              onChange={(e) => setRedeem(e.target.value)}
              placeholder="KD-XXXXX-XXXXX"
              style={{ ...fieldStyle, flex: 1, letterSpacing: 1 }}
              aria-label="Feed code"
            />
            <button className="kd-btn-primary" onClick={() => void redeemCode()} disabled={busy || !redeem.trim()}>
              Use code
            </button>
          </div>
        </div>
      )}

      {outgoing.length === 0 && <p style={hintStyle}>This dial isn&rsquo;t feeding anyone.</p>}
      {outgoing.map((f) => (
        <div key={f.id} style={{ ...cardStyle, marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>{f.targetOrganisationName}</span>
            <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}>
              receiving {scopeSummary(f.scopes, noun)} on “{f.targetDialName}”
            </span>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-faint)" }}>{stateText(f)}</div>

          {editing === f.id && !refusal ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <FeedScopeCanvas
                sections={paintSections}
                scopes={draft}
                attributes={attributes}
                openEnded={openEnded}
                onChange={setDraft}
                noun={noun}
              />
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, marginTop: 4 }}>
                <input type="checkbox" checked={openEnded} onChange={(e) => setOpenEndedAcross(e.target.checked)} />
                Also feed anything added to those sections later
              </label>
              <p style={hintStyle}>
                Paint the dial for sections and {noun.many}; tick items below to narrow further.
                Widening a live feed asks them to accept again; narrowing it takes effect straight away.
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="kd-btn-primary"
                  disabled={busy || draft.length === 0}
                  onClick={() =>
                    void run(async () => {
                      const token = getAccessToken()!;
                      await api.setFeedScope(token, f.id, draft);
                      await api.offerFeed(token, f.id, null);
                      setEditing(null);
                    })
                  }
                >
                  {f.state === "Accepted" ? "Save and re-offer" : "Offer this feed"}
                </button>
                <button className="kd-btn" disabled={busy} onClick={() => setEditing(null)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {!refusal && f.state !== "Revoked" && f.state !== "Declined" && (
                <button className="kd-btn" disabled={busy} onClick={() => startEditing(f)}>
                  {f.state === "Draft" ? "Choose what to feed" : "Change what you feed"}
                </button>
              )}
              {(f.state === "Accepted" || f.state === "Offered" || f.state === "Draft") && (
                <button className="kd-btn" disabled={busy} onClick={() => void askToStop(f)}>
                  Stop feeding
                </button>
              )}
            </div>
          )}
        </div>
      ))}

      {stopping && (
        <Modal
          title="Stop this feed?"
          onClose={() => setStopping(null)}
          width={460}
        >
          <p style={{ fontSize: 13.5, color: "var(--text)", margin: "0 0 10px" }}>
            {stopping.direction === "in"
              ? "You'll stop being able to see their dial, and nothing more will come through."
              : "They'll stop being able to see your dial, and nothing more will go out."}
          </p>
          {stopping.impact.valuesRemaining > 0 ? (
            // The half people get wrong. Watching ends cleanly; anything already wired in was copied, and
            // copies do not un-copy.
            <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "0 0 14px" }}>
              <strong style={{ color: "var(--text)" }}>
                {stopping.impact.valuesRemaining} {stopping.impact.valuesRemaining === 1 ? "value" : "values"}
              </strong>{" "}
              this feed already wrote {stopping.direction === "in" ? "onto your dial" : "onto theirs"} will
              stay, frozen at what {stopping.impact.valuesRemaining === 1 ? "it says" : "they say"} now.
              Nothing will change {stopping.impact.valuesRemaining === 1 ? "it" : "them"} again.
            </p>
          ) : (
            <p style={{ ...hintStyle, marginBottom: 14 }}>Nothing has been written across, so nothing stays behind.</p>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="kd-btn-primary"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await api.revokeFeed(getAccessToken()!, stopping.feedId);
                  setStopping(null);
                })
              }
            >
              Stop feeding
            </button>
            <button className="kd-btn" disabled={busy} onClick={() => setStopping(null)}>
              Keep it
            </button>
          </div>
        </Modal>
      )}

      {wiring && (
        <FeedWiringModal
          feedId={wiring}
          instanceId={instanceId}
          detail={detail}
          onClose={() => setWiring(null)}
        />
      )}
    </SettingsCard>
  );
}
